// Sound playback for Telegram Mini App.
//   - Web Audio API (more reliable than HTMLAudio on iOS WebView).
//   - Try opus/webm first (3-4× smaller), fall back to mp3 if decode fails.
//   - Decode each file ONCE into an AudioBuffer; spawn a fresh source per play.
//   - Unlock on first touchend / pointerdown / click / keydown (iOS 17+ best practice).
//   - Master GainNode lets us mute and ramp volume cleanly.

type SoundName = "spin" | "finish";

const SOURCES: Record<SoundName, string[]> = {
  // First entry preferred; rest are decode fallbacks for old WebViews.
  spin: ["/sounds/spin-loop.webm", "/sounds/spin-loop.mp3"],
  finish: ["/sounds/finish.webm", "/sounds/finish.mp3"],
};

const MASTER_VOLUME = 0.3;
const STORAGE_KEY = "saaslots:muted";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
const buffers: Partial<Record<SoundName, AudioBuffer>> = {};
let preloadStarted = false;

let spinSource: AudioBufferSourceNode | null = null;
let spinGain: GainNode | null = null;

let muted = false;
let unlocked = false;

/**
 * HTMLAudio fallback element pool. Used on platforms where Web Audio is
 * silently blocked (notably Telegram Desktop on macOS as of 2026-05).
 * Re-uses the same <audio> element per source so we don't decode repeatedly.
 */
const htmlAudioPool: Partial<Record<SoundName, HTMLAudioElement>> = {};
let htmlAudioPrimed = false;

function getHtmlAudio(name: SoundName): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  if (htmlAudioPool[name]) return htmlAudioPool[name]!;
  const el = new Audio();
  el.preload = "auto";
  el.crossOrigin = "anonymous";
  // Pick the first source the browser thinks it can play.
  const url = SOURCES[name].find((u) => {
    const ext = u.split(".").pop() ?? "";
    return el.canPlayType(ext === "webm" ? "audio/webm" : "audio/mpeg");
  }) ?? SOURCES[name][SOURCES[name].length - 1];
  el.src = url;
  el.volume = MASTER_VOLUME;
  htmlAudioPool[name] = el;
  return el;
}

/**
 * Telegram Desktop blocks AudioContext silently on some builds. We "prime"
 * <audio> elements during the unlock gesture so subsequent .play() calls
 * work without an additional gesture.
 */
function primeHtmlAudio(): void {
  if (htmlAudioPrimed) return;
  htmlAudioPrimed = true;
  for (const name of Object.keys(SOURCES) as SoundName[]) {
    const el = getHtmlAudio(name);
    if (!el) continue;
    // Setting muted + tiny play + immediate pause registers user-gesture
    // permission. CRITICAL: keep `muted=true` after pause. Earlier code
    // unmuted in the .then() callback, which on iOS opened a race where
    // the next tap (anywhere) could trigger a muted-true → muted-false
    // transition on a still-buffering loop and audibly play the spin
    // sample. The element only un-mutes when an actual play call wants
    // it (`playFinish` / `startSpinLoop`).
    el.muted = true;
    el.play()
      .then(() => {
        el.pause();
        el.currentTime = 0;
        // INTENTIONALLY do not set muted = false here.
      })
      .catch(() => {
        /* gesture not registered; will retry on next interaction */
        htmlAudioPrimed = false;
      });
  }
}

try {
  muted = localStorage.getItem(STORAGE_KEY) === "1";
} catch {
  /* ignore */
}

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const AC = (window.AudioContext || (window as any).webkitAudioContext) as
    | typeof AudioContext
    | undefined;
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch {
    return null;
  }
  master = ctx.createGain();
  master.gain.value = muted ? 0 : MASTER_VOLUME;
  master.connect(ctx.destination);
  void preload();
  return ctx;
}

async function decodeFirstAvailable(urls: string[]): Promise<AudioBuffer | null> {
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const ab = await res.arrayBuffer();
      return await ctx!.decodeAudioData(ab);
    } catch (err) {
      console.warn(`audio decode failed for ${url}:`, err);
    }
  }
  return null;
}

async function preload(): Promise<void> {
  if (preloadStarted || !ctx) return;
  preloadStarted = true;
  await Promise.all(
    (Object.keys(SOURCES) as SoundName[]).map(async (name) => {
      const buf = await decodeFirstAvailable(SOURCES[name]);
      if (buf) buffers[name] = buf;
    }),
  );
}

export function unlockAudio(): void {
  // Always prime the HTMLAudio pool too, Telegram Desktop tends to swallow
  // Web Audio silently while still allowing <audio>.
  primeHtmlAudio();
  if (unlocked) return;
  const c = getCtx();
  if (!c) return;
  try {
    const b = c.createBuffer(1, 1, 22050);
    const s = c.createBufferSource();
    s.buffer = b;
    s.connect(c.destination);
    s.start(0);
  } catch {
    /* ignore */
  }
  c.resume()
    .then(() => {
      if (c.state === "running") unlocked = true;
    })
    .catch(() => {});
}

export function installAudioUnlocker(): void {
  if (typeof window === "undefined") return;
  const events = ["touchend", "pointerdown", "click", "keydown"] as const;
  const handler = async () => {
    unlockAudio();
    await Promise.resolve();
    if (unlocked) events.forEach((e) => document.body.removeEventListener(e, handler));
  };
  events.forEach((e) => document.body.addEventListener(e, handler, { passive: true }));
}

export function setMuted(m: boolean): void {
  muted = m;
  try {
    localStorage.setItem(STORAGE_KEY, m ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (master && ctx) {
    const t = ctx.currentTime;
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value, t);
    master.gain.linearRampToValueAtTime(m ? 0 : MASTER_VOLUME, t + 0.08);
  }
  // Sync HTMLAudio volumes too, they're a separate channel from Web Audio.
  for (const name of Object.keys(htmlAudioPool) as SoundName[]) {
    const el = htmlAudioPool[name];
    if (el) el.volume = m ? 0 : MASTER_VOLUME;
  }
  if (m) stopSpinLoop();
  else if (ctx?.state === "suspended") ctx.resume().catch(() => {});
}

export function isMuted(): boolean {
  return muted;
}

function ensureRunning(): boolean {
  if (!ctx || !master) return false;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx.state === "running";
}

export function playFinish(): void {
  if (muted) return;
  // Web Audio path first, lower latency, better volume control.
  const c = getCtx();
  if (c && master && buffers.finish) {
    ensureRunning();
    const src = c.createBufferSource();
    src.buffer = buffers.finish;
    src.connect(master);
    try {
      src.start(0);
      return;
    } catch {
      /* fall through to HTML audio */
    }
  }
  // Fallback: HTMLAudio (Telegram Desktop and other gesture-strict WebViews).
  const el = getHtmlAudio("finish");
  if (el) {
    el.muted = false;
    el.currentTime = 0;
    el.volume = MASTER_VOLUME;
    el.play().catch(() => {});
  }
}

export function startSpinLoop(): void {
  if (muted) return;
  const c = getCtx();
  if (c && master && buffers.spin && !spinSource) {
    ensureRunning();
    const gain = c.createGain();
    gain.gain.setValueAtTime(0, c.currentTime);
    const src = c.createBufferSource();
    src.buffer = buffers.spin;
    src.loop = true;
    src.connect(gain).connect(master);
    try {
      src.start(0);
      gain.gain.linearRampToValueAtTime(1, c.currentTime + 0.08);
      spinSource = src;
      spinGain = gain;
      return;
    } catch {
      /* fall through */
    }
  }
  // Fallback: HTMLAudio loop.
  const el = getHtmlAudio("spin");
  if (el) {
    el.loop = true;
    el.muted = false;
    el.currentTime = 0;
    el.volume = MASTER_VOLUME;
    el.play().catch(() => {});
  }
}

export function stopSpinLoop(): void {
  // Always stop the HTMLAudio fallback in case it was used.
  const el = htmlAudioPool.spin;
  if (el && !el.paused) {
    try {
      el.pause();
      el.currentTime = 0;
      // Defensive: park back in muted state so a later body-level prime
      // (after a fresh focus or tab switch) cannot audibly fire.
      el.muted = true;
    } catch {
      /* ignore */
    }
  }
  if (!ctx || !spinSource || !spinGain) return;
  const t = ctx.currentTime;
  const src = spinSource;
  const gain = spinGain;
  spinSource = null;
  spinGain = null;
  try {
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(gain.gain.value, t);
    gain.gain.linearRampToValueAtTime(0, t + 0.12);
    src.stop(t + 0.14);
  } catch {
    /* ignore */
  }
}

/**
 * Anticipation hum, short rising tone while the slow-mo reel decides.
 * Synthesized so it works even before sounds are preloaded.
 */
export function playAnticipation(durationMs = 900): void {
  if (muted) return;
  const c = getCtx();
  if (!c || !master) return;
  ensureRunning();
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(110, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(440, c.currentTime + durationMs / 1000);
  g.gain.setValueAtTime(0, c.currentTime);
  g.gain.linearRampToValueAtTime(0.35, c.currentTime + 0.08);
  g.gain.linearRampToValueAtTime(0, c.currentTime + durationMs / 1000);
  osc.connect(g).connect(master);
  osc.start();
  osc.stop(c.currentTime + durationMs / 1000 + 0.05);
}
